import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const TrialBalance: React.FC = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      // Call trial balance API
      // const response = await accountingService.getTrialBalance({ from_date: fromDate, to_date: toDate });
      // setData(response);
      setData({ accounts: [], totals: { debit: 0, credit: 0 } });
    } catch (err: any) {
      setError(err.message || 'Failed to generate trial balance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const isBalanced = data && data.totals.debit === data.totals.credit;

  return (
    <Container>
      <Header>
        <div>
          <BackLink to="/accounting">← Back to Accounting</BackLink>
          <h1>Trial Balance</h1>
          <Subtitle>Summary of all account balances — total debits must equal total credits</Subtitle>
        </div>
      </Header>

      <FilterSection>
        <FilterRow>
          <FormGroup>
            <label>From Date</label>
            <DateInput
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <label>To Date</label>
            <DateInput
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </FormGroup>
          <GenerateButton onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating...' : '📊 Generate Report'}
          </GenerateButton>
          <PrintButton onClick={() => window.print()}>
            🖨️ Print
          </PrintButton>
        </FilterRow>
      </FilterSection>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {data && (
        <>
          <BalanceIndicator $balanced={isBalanced}>
            {isBalanced ? (
              <><span>✓</span> Trial Balance is Balanced</>
            ) : (
              <><span>⚠</span> Trial Balance is OUT OF BALANCE</>
            )}
          </BalanceIndicator>

          <ReportCard>
            <ReportHeader>
              <h2>Trial Balance</h2>
              <p>Period: {fromDate} to {toDate}</p>
            </ReportHeader>

            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: '100px' }}>Code</Th>
                    <Th>Account Name</Th>
                    <Th>Type</Th>
                    <Th align="right">Debit</Th>
                    <Th align="right">Credit</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.length === 0 ? (
                    <tr>
                      <Td colSpan={5}>
                        <EmptyState>No transactions in the selected period</EmptyState>
                      </Td>
                    </tr>
                  ) : (
                    data.accounts.map((account: any) => (
                      <Tr key={account.id}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{account.code}</Td>
                        <Td><strong>{account.name}</strong></Td>
                        <Td>
                          <TypeBadge $type={account.account_type}>
                            {account.account_type}
                          </TypeBadge>
                        </Td>
                        <Td align="right" style={{ fontFamily: 'monospace' }}>
                          {account.debit > 0 ? formatCurrency(account.debit) : '—'}
                        </Td>
                        <Td align="right" style={{ fontFamily: 'monospace' }}>
                          {account.credit > 0 ? formatCurrency(account.credit) : '—'}
                        </Td>
                      </Tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td colSpan={3}><strong>TOTAL</strong></Td>
                    <Td align="right">
                      <TotalAmount>{formatCurrency(data.totals.debit)}</TotalAmount>
                    </Td>
                    <Td align="right">
                      <TotalAmount>{formatCurrency(data.totals.credit)}</TotalAmount>
                    </Td>
                  </TotalRow>
                  {!isBalanced && (
                    <DifferenceRow>
                      <Td colSpan={3}><strong>Difference</strong></Td>
                      <Td colSpan={2} align="right">
                        <DifferenceAmount>
                          {formatCurrency(Math.abs(data.totals.debit - data.totals.credit))}
                        </DifferenceAmount>
                      </Td>
                    </DifferenceRow>
                  )}
                </tfoot>
              </Table>
            </TableContainer>
          </ReportCard>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 24px;

  h1 {
    font-size: 28px;
    color: #333;
    margin: 10px 0;
  }
`;

const BackLink = styled(Link)`
  color: #667eea;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: #555;
  }
`;

const DateInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const GenerateButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrintButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: #333;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #f9fafb;
    border-color: #667eea;
  }
`;

const ErrorAlert = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  color: #991b1b;
  font-size: 14px;
`;

const BalanceIndicator = styled.div<{ $balanced: boolean }>`
  background: ${props => props.$balanced ? '#d1fae5' : '#fee2e2'};
  border: 2px solid ${props => props.$balanced ? '#10b981' : '#ef4444'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$balanced ? '#065f46' : '#991b1b'};
  text-align: center;

  span {
    font-size: 20px;
    margin-right: 8px;
  }
`;

const ReportCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 24px;
  border-bottom: 2px solid #e5e7eb;

  h2 {
    font-size: 24px;
    color: #333;
    margin: 0 0 8px 0;
  }

  p {
    color: #666;
    font-size: 14px;
    margin: 0;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ align?: string }>`
  text-align: ${props => props.align || 'left'};
  padding: 16px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`;

const Tr = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:not(:last-child) td {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const Td = styled.td<{ align?: string }>`
  padding: 14px 16px;
  font-size: 14px;
  color: #333;
  text-align: ${props => props.align || 'left'};
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$type) {
      case 'asset': return '#dbeafe';
      case 'liability': return '#fee2e2';
      case 'equity': return '#e0e7ff';
      case 'revenue': return '#d1fae5';
      case 'expense': return '#fef3c7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'asset': return '#1e40af';
      case 'liability': return '#991b1b';
      case 'equity': return '#5b21b6';
      case 'revenue': return '#065f46';
      case 'expense': return '#92400e';
      default: return '#374151';
    }
  }};
`;

const TotalRow = styled.tr`
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
  font-weight: 700;
`;

const TotalAmount = styled.strong`
  font-family: monospace;
  font-size: 16px;
  color: #333;
`;

const DifferenceRow = styled.tr`
  background: #fee2e2;
  border-top: 1px solid #fecaca;
`;

const DifferenceAmount = styled.strong`
  font-family: monospace;
  font-size: 16px;
  color: #991b1b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
`;

export default TrialBalance;
